import React from 'react'
import { graphql } from 'gatsby'
import Helmet from 'react-helmet'
import createMeta from '../utils/meta-creator'
import styled from 'styled-components'
import { Flex, Box } from 'rebass'
import citygateTheme from '../theme/'
import typography from '../utils/typography'
import ContentBrandDerivativeSpec from '../components/ContentBrandDerivativeSpec'
import ContentBrandDerivativeVanSpec from '../components/ContentBrandDerivativeVanSpec'
import ContentBrandHero from '../components/ContentBrandHero'
import ContentVehicleMultiCardListing from '../components/ContentVehicleMultiCardListing'
import ContentSectionHeader from '../components/ContentSectionHeader'
import ContentFinanceTabs from '../components/ContentFinanceTabs'
import ContentAccordion from '../components/ContentAccordion'
import ContentFeatureList from '../components/ContentFeatureList'
import ContentFullImage from '../components/ContentFullImage'
import ContentBar from '../components/ContentBar'
import LinkRounded from '../components/LinkRounded'
import TestDriveModal from '../components/TestDriveModal'
import createModelNormaliser from '../../lib/model/model-to-vehicle-card-normaliser'
import { shuffle } from 'lodash'
import { connect } from 'react-redux'
import { open } from '../state/test-drive-modal/actions'
import htmlStripper from '../../lib/html-stripper'
import Layout from '../components/Layout'
import createDebug from 'debug'
import { isVan } from '../../lib/category-matcher'
import { Container } from '../utils/style'

const debug = createDebug('submodel')

const FlexWrapper = styled(Flex)`
  align-items: center;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.blue};
  width: 100%;
`
const joinLabelAndValue = ({ label, value }) =>
  label + (value ? ': ' + value : '')

const detailGroups = [
  { id: 'interior', label: 'Interior' },
  { id: 'exterior', label: 'Exterior' },
  { id: 'environment', label: 'Environmental' },
  { id: 'technical', label: 'Technical' }
]

const getModelsYouMightLike = (thisModel, models) => {
  const shuffled = shuffle(models)
  let returnModels = shuffled.filter((model) => !model.parentModel)
  if (returnModels.length < 3)
    returnModels = shuffled.filter((model) => !!model.parentModel)
  return returnModels.filter((model) => model._id !== thisModel._id).slice(0, 3)
}

const CenterControls = styled(Box)`
  text-align: center;
  width: 100%;
  margin: -5px;

  a {
    margin: 5px;
  }
`

const Submodel = ({ pageContext, data, location, onTestDriveClick }) => {
  const { make, offers } = pageContext
  const submodel = data.allModelCatfish && data.allModelCatfish.edges[0].node
  if (!submodel) {
    debug('No submodel found')
    return null
  }
  const allModels =
    data.otherModels && data.otherModels.edges.map(({ node }) => node)
  const locations =
    data.locations && data.locations.edges.map(({ node }) => node)
  const otherModels = getModelsYouMightLike(submodel, allModels).map(
    createModelNormaliser(make, allModels)
  )
  const metaTitle = `${make.name} ${submodel.name}`
  const metaDescription = htmlStripper(submodel.description)

  const brands = new Set(
    locations.reduce(
      (acc, location) => [...acc, ...location.vehicles.map((v) => v.name)],
      []
    )
  )
  const brandSpecific = brands.size === 1
  const brand = brands.values().next().value

  const locationNames = locations.map((location) => location.shortName)
  const singleLocation = locations[0]

  const contextCallOutImage =
    pageContext && pageContext.submodel && pageContext.submodel.calloutImage

  const contextCallOutLink =
    pageContext && pageContext.submodel && pageContext.submodel.calloutImage

  const offerSlides =
    offers &&
    offers
      .map((o) =>
        o.sell
          ? { text: o.sell, link: o.callToActionLink || o.slug }
          : undefined
      )
      .filter((s) => s !== undefined)

  return (
    <Layout location={location} category={make.category} highlightNew>
      <TestDriveModal
        newVehicle
        brandSpecific={brandSpecific}
        brand={brand}
        vehicleId={submodel._id}
        locations={locationNames}
        location={singleLocation}
      />
      <Helmet
        title={metaTitle}
        meta={createMeta({ ...submodel, metaTitle, metaDescription })}
      />
      <FlexWrapper flexWrap="wrap">
        {offerSlides && offerSlides.length !== 0 && (
          <ContentBar offer slides={offerSlides} />
        )}
        <ContentBrandHero
          dark
          compact
          title={submodel.name}
          x
          price={submodel.price}
          make={make.name}
          img={submodel.heroImages[0]}
          bg={submodel.backgroundImages[0]}
          calloutImage={contextCallOutImage}
          calloutLink={contextCallOutLink}
        />
        {isVan(submodel.category) ? (
          [
            <CenterControls key="links" pb={[typography.rhythm(1), '10vh']}>
              <LinkRounded reversed onClick={onTestDriveClick}>
                Register Interest
              </LinkRounded>
              {submodel.brochureUrl && (
                <LinkRounded href={submodel.brochureUrl} outline>
                  Download Brochure
                </LinkRounded>
              )}
            </CenterControls>,
            <ContentBrandDerivativeVanSpec
              key="spec"
              items={submodel.dimensions}
              make={make}
              model={submodel}
            />
          ]
        ) : (
          <ContentBrandDerivativeSpec
            specifications={submodel.specifications}
            make={make}
            model={submodel}
            vehicleId={submodel._id}
            onTestDriveClick={onTestDriveClick}
            locations={locationNames}
            brandSpecific={brandSpecific}
            brand={brand}
            location={singleLocation}
            isNewVehicle={true}
          />
        )}
      </FlexWrapper>

      <ContentFullImage
        image={submodel.lifestyleImages[0]}
        alt={submodel.name}
      />

      <Container
        as="section"
        px={0}
        mx={'8%'}
        mt={[0, typography.rhythm(2)]}
        mb={[typography.rhythm(3), typography.rhythm(4)]}
        maxWidth="initial"
      >
        {detailGroups.map(
          ({ id, label }) =>
            submodel[id].length > 0 && (
              <ContentAccordion title={label} id={id} key={id}>
                <ContentFeatureList
                  list={submodel[id]
                    .filter((item) => item.value !== null)
                    .map(joinLabelAndValue)}
                />
              </ContentAccordion>
            )
        )}
      </Container>

      {submodel.capCode && (
        <ContentFinanceTabs registration={submodel.capCode} />
      )}

      {otherModels && otherModels.length > 0 && (
        <Box as="section" mb={typography.rhythm(1)}>
          <ContentSectionHeader
            title={`Other ${submodel.category}s you might like`}
            fg={citygateTheme.colors.blue}
          >
            <p>
              Browse other models from this range, you might just find what you
              are looking for…
            </p>
          </ContentSectionHeader>
          <ContentVehicleMultiCardListing vehicles={otherModels} />
        </Box>
      )}
    </Layout>
  )
}

const mapDispatchToProps = (dispatch) => ({
  onTestDriveClick: () => {
    dispatch(open())
  }
})

export default connect(null, mapDispatchToProps)(Submodel)

export const pageQuery = graphql`
  query SubmodelQuery($id: String!, $makeId: String!, $category: String!) {
    otherModels: allModelCatfish(
      filter: { make: { eq: $makeId }, category: { eq: $category } }
    ) {
      edges {
        node {
          _id
          name
          slug
          thumbnailImages
          price
          category
          parentModel
        }
      }
    }
    locations: allDealershipCatfish(
      filter: {
        disableVehicleAllocating: { eq: false }
        vehicles: { elemMatch: { _id: { eq: $makeId } } }
      }
      sort: { shortName: ASC }
    ) {
      edges {
        node {
          slug
          shortName
          location
          vehicles {
            name
          }
        }
      }
    }
    allModelCatfish(filter: { _id: { eq: $id }, state: { eq: "Published" } }) {
      edges {
        node {
          _id
          name
          description
          heroImages
          backgroundImages
          lifestyleImages
          capCode
          category
          parentModel
          price
          brochureUrl
          interior {
            label
            value
          }
          exterior {
            label
            value
          }
          environment {
            label
            value
          }
          technical {
            label
            value
          }
          dimensions {
            heading
            subheading
            description
            images
            options
            configurationUrl
            brochureUrl
          }
          specifications {
            name
            label
            price
            description
            doors
            engineSize
            transmission
            efficiency
            performance
            brochureUrl
            images
          }
        }
      }
    }
  }
`
